import { useEffect, useState } from 'react';
import { api } from '../../../api';

/**
 * The source's `useAuth()`, over the platform's session.
 *
 * THE SEAM (§1.5, open question Q7)
 * ---------------------------------
 * APR's screens ask `hasPermission('canManageSettings')` against six permission keys the
 * platform does not have. The platform has roles, an `isAdmin` flag, and per-module seat
 * assignment. The mapping below is deliberately conservative — it errs toward the
 * source's intent rather than toward openness, because getting this wrong silently grants
 * writes rather than blocking reads:
 *
 *   canManageSettings   -> administrator (matches the server's `requireRole` mapping)
 *   canManageUsers      -> administrator
 *   canViewDashboard    -> any authenticated user
 *   canViewAllMenus     -> any authenticated user
 *   canViewAPRScoring   -> any authenticated user
 *   canRunAPRAnalysis   -> any authenticated user
 *
 * Reaching an APR screen at all already means the module is licensed AND assigned — the
 * server enforces that on every `/api/apr` route — so "any authenticated user" here means
 * "any APR seat-holder", not "anyone".
 *
 * The UI check is a convenience, never the boundary: the server re-checks independently
 * in `apr/requireRole.ts`. A wrong answer here hides or shows a button; it does not
 * decide what the API will accept.
 */

const ADMIN_ONLY = new Set(['canManageSettings', 'canManageUsers']);

export interface AprPermissions {
  hasPermission: (key: string) => boolean;
  /**
   * `id` is the platform's user id, passed through unmapped. Final Disposition writes it
   * to `changed_by_user_id` on every disposition change, which is what the Audit Trail
   * screen reads back — without it every entry would be attributed to 'unknown'.
   *
   * `role` is here because the ported screens compare it directly —
   * `currentUser?.role === 'Administrator'` gates the destructive actions on License
   * Intelligence. It is MAPPED, not passed through: the platform's admin flag becomes
   * the source's 'Administrator' string, so the source's comparison keeps working and
   * keeps meaning the same thing. Anything else reports the platform role verbatim.
   */
  currentUser: { id: string; name: string; email: string; role: string } | null;
}

export function useAuth(): AprPermissions {
  const [user, setUser] = useState<{
    id: string;
    name: string;
    email: string;
    isAdmin: boolean;
    role: string;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .session()
      .then((s) => {
        if (cancelled || !s.user) return;
        const isAdmin = Boolean(s.user.isAdmin || s.user.canAccessTalogent);
        setUser({
          id: s.user.id,
          name: s.user.displayName,
          email: s.user.email,
          isAdmin,
          // PRODUCT role first, platform role second.
          //
          // `roles` is [platformRole, ...productRoleKeys], so roles[0] is always the
          // platform role — 'Standard User' for an invited owner. The cloned screens
          // compare this against the source's strings ('Application Owner',
          // 'APR Analyst'), so reading roles[0] meant those comparisons NEVER matched:
          // ApplicationPortfolioQA's "only my applications" filter was dead code, and in
          // Flat view the owner got no Start Q&A button at all.
          //
          // Mapped to the source's own vocabulary so the cloned screens are unchanged.
          role: isAdmin
            ? 'Administrator'
            : s.user.roles?.includes('application_owner')
              ? 'Application Owner'
              : s.user.roles?.includes('apr_analyst')
                ? 'APR Analyst'
                : (s.user.roles?.[0] ?? ''),
        });
      })
      .catch(() => {
        /* the shell already surfaces session failures */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return {
    hasPermission: (key: string) => (ADMIN_ONLY.has(key) ? Boolean(user?.isAdmin) : Boolean(user)),
    currentUser: user
      ? { id: user.id, name: user.name, email: user.email, role: user.role }
      : null,
  };
}
